// public/js/utils/rssi-level.js
// Robust RSSI level (`rssiAvg`) for BLE beacons: ONE pure module, used by the
// Lambda at ingest AND by the browser when it draws the signal chart, exactly
// like battery-usage.js. The number stored at ingest and the curve on the chart
// must be the same number, so there is only one implementation of it.
//
// WHY (docs/ble-sensors.md "Signal level"):
// A single RSSI reading is close to useless for "is an engine sitting on this
// stand". The raw series of a beacon mounted across the stand from its tracker
// jitters a few dB from read to read, and real production data is often
// BIMODAL - the same pair alternating between two propagation states an hour
// apart (e.g. -48 and -64 dBm). What DOES carry the information is the LEVEL:
// mounting an engine between tag and tracker drops it by 10-15 dB and keeps it
// there for days.
//
// A plain MEDIAN is the obvious robust average and it is the wrong one here: on
// a 50/50 bimodal window it sits on the boundary and flips the full 15 dB
// between the two clusters with every new sample - the exact thing it was meant
// to suppress (measured on a production tag: 23 jumps of >= 4 dB in a month).
// A TRIMMED MEAN - drop the extreme samples on BOTH sides, average the rest -
// is what the job actually asks for: it ignores outliers in either direction
// and still moves smoothly when the level genuinely shifts. On the same data it
// produced zero jumps >= 4 dB (worst single step 2.6 dB) while keeping the full
// idle-to-loaded separation (-45 -> -62 dBm), and it crosses the midpoint of a
// step about half a window after the change.
//
// Scoped PER (beacon, gateway) pair, because receive quality is a property of
// the pair, not of the tag: a second tracker 100 m away reads the same beacon at
// -96 dBm, and on a busy day that is 40% of the rows. Mixed into one window
// those reads would drag the level around for reasons that have nothing to do
// with this stand. Only the OWNER pair gets a value; a far-away gateway's rows
// carry none.
//
// WHICH PAIR OWNS IT: the gateway on the SAME ASSET as the beacon, when the
// beacon's asset has one - not merely the strongest. Those two rules pick the
// same pair in normal operation (measured on 1973 production rows across 41
// days: the foreign tracker was never the strongest, not once), and they come
// apart exactly where it matters. When the asset's own tracker falls silent past
// maxAgeMs and a tracker on the next stand keeps hearing the tag, "strongest"
// hands ownership to that foreign pair and the level drops from -44 to -95 dBm -
// a 50 dB cliff that is not a signal change at all, and that fires every
// "engine loaded" rule thresholded on the level. Restricting ownership to the
// asset's own gateways turns that into no value, which is the honest answer:
// nothing on this asset is listening. A beacon whose asset has NO tracker of its
// own (located purely by neighbouring gateways) keeps the strongest-wins rule,
// so it still gets a level.
//
// Carried as a flag on each stored sample rather than looked up, so the decision
// costs no extra read in the Lambda and ages out on its own: a tracker moved to
// another asset stops flagging within one maxAgeMs, and windows stored before
// this existed simply fall back to strongest-wins until they refill.

export const RSSI_LEVEL_DEFAULTS = {
  // Samples per (beacon, gateway) window. Bigger = smoother but slower: a step
  // is fully followed after about one window, and crosses the midpoint after
  // about half. 11 samples at the typical 1/h beacon cadence means a change is
  // unambiguous within ~6 h.
  window: 11,
  // Fraction dropped from EACH end before averaging (0.2 of 11 -> drop 2 low +
  // 2 high, mean the middle 7). 0 would be a plain mean.
  trim: 0.2,
  // Below this many samples there is no level at all, rather than a figure
  // built from two readings that happens to look like one.
  minSamples: 5,
  // A sample older than this is not evidence about the level now. Also what
  // makes a beacon that was silent for days start clean instead of blending
  // across the gap.
  maxAgeMs: 36 * 60 * 60 * 1000,
  // Windows kept per beacon. Enough for a tag that a few trackers can hear;
  // the least recently heard gateway is dropped first.
  maxGateways: 4,
  // How hard an unstable link is charged against its level when forming
  // `rssiFloor` (see floorOf). 0.5 x IQR below a median is the 25th percentile
  // of a symmetric window, which is why the floor reads as "the level three
  // readings in four beat". Measured on production data, 0.5 keeps the full
  // idle-to-loaded separation; 1.0 starts to blur it on a tag whose spread is
  // high for reasons other than load.
  spreadK: 0.5,
  // How long the beacon REMEMBERS that a gateway on its own asset exists.
  // Deliberately far longer than maxAgeMs, and this is the whole point: the
  // sample windows forget a silent tracker after 36 h, and if that were also the
  // memory of WHO owns the level, a tracker that goes quiet would silently hand
  // the level to a tracker on the next stand - the exact 50 dB cliff this is
  // meant to prevent. Thirty days still forgets a tracker genuinely moved to
  // another asset, long after its windows have gone.
  homeMemoryMs: 30 * 24 * 60 * 60 * 1000,
};

const GW_NONE = '-';   // a read with no forwarding gateway still has a window

const gwKey = (g) => (g == null || g === '' ? GW_NONE : String(g));

// A reading, or null. Number(null) is 0 and Number('') is 0 - and a beacon with
// no signal reading stores rssi: null, so a bare Number() would fold a missing
// read into the window as a perfect 0 dBm and wreck the level.
function dbm(v) {
  if (v == null || v === '') return null;
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
}

/** One decimal. Keeps the stored value, the chart and a rule threshold comparable. */
const round1 = (n) => Math.round(n * 10) / 10;

/**
 * Mean of `values` with the extreme `trim` fraction removed from each end.
 * Returns null for an empty list. Never throws.
 */
export function trimmedMean(values, trim = RSSI_LEVEL_DEFAULTS.trim) {
  const nums = (values || []).map(dbm).filter(v => v != null);
  if (!nums.length) return null;
  const sorted = nums.slice().sort((a, b) => a - b);
  // floor, so the trim only bites once the window is big enough to spare the
  // samples - and the kept slice is never empty.
  const k = Math.floor(sorted.length * Math.max(0, Math.min(0.49, Number(trim) || 0)));
  const kept = sorted.length - 2 * k >= 1 ? sorted.slice(k, sorted.length - k) : sorted;
  return round1(kept.reduce((a, b) => a + b, 0) / kept.length);
}

/**
 * Value at fraction `p` of a sorted list, linearly interpolated.
 * Not exported: only the quartiles below need it.
 */
function quantile(sorted, p) {
  const i = (sorted.length - 1) * p;
  const lo = Math.floor(i);
  const hi = Math.min(lo + 1, sorted.length - 1);
  return sorted[lo] + (sorted[hi] - sorted[lo]) * (i - lo);
}

/**
 * SPREAD of a window, in dB: the interquartile range (P75 - P25) of the
 * readings. Returns null for an empty list.
 *
 * Why IQR and not a standard deviation: the window can hold a deep fade or a
 * transition straddling two propagation states, and an SD squares exactly those
 * samples into dominance. The IQR describes the BULK of the readings and is the
 * natural partner to the trimmed mean - both answer "what is this link doing
 * most of the time", one about position, one about width. It is also readable:
 * 1 dB is a link you could thread a needle with, 8 dB is one with something
 * moving through it.
 */
export function spreadOf(values) {
  const nums = (values || []).map(dbm).filter(v => v != null);
  if (!nums.length) return null;
  const sorted = nums.slice().sort((a, b) => a - b);
  return round1(quantile(sorted, 0.75) - quantile(sorted, 0.25));
}

/**
 * The level after charging it for its own instability:
 *   floor = level - k * spread
 *
 * WHY THIS AND NOT A "QUALITY SCORE" (docs/ble-sensors.md section 5):
 * The obvious combination - normalise level and spread to 0..100 and average
 * them - was measured against a month of production data and it DESTROYS the
 * thing it was meant to improve. The level alone separates idle from loaded on
 * BLE_2CD3AD0BD724 with zero overlap (effect size 10.3); a 50/50 score drops
 * that to 3.5, and on the second tag the score overlaps 98% - a detector that
 * works turned into one that does not. Spread is the noisier variable, and
 * mixing a noisy variable into a clean one adds variance, not information.
 *
 * Subtracting a FRACTION of the spread keeps what is useful about the idea
 * without that cost: the number stays in dBm, so a threshold carries over from
 * the level unchanged, and it reads as a lower bound - a link that is both weak
 * AND erratic scores worse than one that is merely weak. Measured: zero overlap
 * preserved on D724, 10% on 0E88 (vs 6% for the level alone).
 */
export function floorOf(level, spread, k = RSSI_LEVEL_DEFAULTS.spreadK) {
  if (level == null) return null;
  const s = dbm(spread);
  const kk = Number(k);
  if (s == null || !Number.isFinite(kk)) return round1(level);
  return round1(level - kk * s);
}

/** The level of one window, or null while it is too short. */
export function levelOf(samples, opts = {}) {
  const o = { ...RSSI_LEVEL_DEFAULTS, ...opts };
  const list = Array.isArray(samples) ? samples : [];
  if (list.length < o.minSamples) return null;
  return trimmedMean(list.map(s => s?.v), o.trim);
}

/**
 * Append one sample to a window, then prune it by age and length. New array.
 * `home` marks a read forwarded by a gateway on the beacon's own asset; it is
 * stored as `h: 1` and only when true, so the window kept on the device row does
 * not grow a key per sample for the common case.
 */
export function pushSample(samples, t, v, opts = {}, home = false) {
  const o = { ...RSSI_LEVEL_DEFAULTS, ...opts };
  const next = [...(Array.isArray(samples) ? samples : []), { t, v, ...(home ? { h: 1 } : {}) }]
    .filter(s => s && Number.isFinite(s.t) && Number.isFinite(s.v) && t - s.t <= o.maxAgeMs);
  return next.slice(-o.window);
}

/**
 * The beacon's own-asset gateways, as a Set of keys.
 *
 * Two input shapes, because the two callers know this in different ways. The
 * Lambda sees one gateway per uplink and persists what it learned as a map
 * `{ gatewayId: lastSeenMs }`, pruned by homeMemoryMs. The browser holds the
 * whole history at once and simply passes the list it read off the non-BLE rows,
 * which carries no timestamps and never expires within the window.
 */
function homeSetOf(src, t, maxMs) {
  const out = new Set();
  if (Array.isArray(src)) {
    for (const g of src) if (g != null && g !== '') out.add(gwKey(g));
    return out;
  }
  for (const [g, ts] of Object.entries(src || {})) {
    const n = Number(ts);
    if (!Number.isFinite(n) || t - n <= maxMs) out.add(gwKey(g));
  }
  return out;
}

/** The home-gateway memory to persist after this reading. New object. */
function nextHomeMemory(src, key, home, t, maxMs) {
  const mem = {};
  if (src && !Array.isArray(src) && typeof src === 'object') {
    for (const [g, ts] of Object.entries(src)) {
      const n = Number(ts);
      if (Number.isFinite(n) && t - n <= maxMs) mem[g] = n;
    }
  }
  if (home) mem[key] = t;
  return mem;
}

/**
 * Fold one beacon reading into its per-gateway windows.
 *
 * @param {object} windows  { [gatewayId]: [{t,v,h?}] } as stored on the device row
 * @param {{gateway: string|null, t: number, v: number, home?: boolean,
 *          homeGateways?: object|string[]}} sample
 *   ms + dBm, whether that gateway sits on the beacon's own asset, and the
 *   own-asset gateways known so far (the map to persist, or an explicit list)
 * @returns {{windows: object, level: number|null, spread: number|null, floor: number|null,
 *            owner: string|null, isOwner: boolean, levels: object, home: boolean,
 *            homeGateways: object}}
 *   `level`, `spread` and `floor` are non-null ONLY when this reading's gateway
 *   is the owner, so a caller can stamp them without deciding anything itself.
 *
 * Owner = the strongest level among the beacon's OWN-ASSET gateways, or among
 * all of them when the asset has no gateway of its own (see the header). Ties
 * broken by id. Deliberately a pure function of the sample history and nothing
 * else: the browser replays the same calls over a fetched history and has to
 * arrive at the identical owner and the identical value, which it cannot do if
 * the rule depends on stored state like the position-stickiness in the ingest
 * path.
 */
export function updateRssiLevel(windows, sample, opts = {}) {
  const o = { ...RSSI_LEVEL_DEFAULTS, ...opts };
  const t = dbm(sample?.t);
  const v = dbm(sample?.v);
  const key = gwKey(sample?.gateway);
  const home = !!sample?.home;
  const src = (windows && typeof windows === 'object') ? windows : {};
  if (t == null || v == null) {
    return {
      windows: src, level: null, spread: null, floor: null, owner: null,
      isOwner: false, levels: {}, home,
      homeGateways: (sample?.homeGateways && !Array.isArray(sample.homeGateways)) ? sample.homeGateways : {},
    };
  }
  const homeMem = nextHomeMemory(sample?.homeGateways, key, home, t, o.homeMemoryMs);

  const next = {};
  for (const [g, list] of Object.entries(src)) {
    if (!Array.isArray(list)) continue;
    // Drop a gateway that has not heard this beacon within the window's memory,
    // so a tracker that drove off does not stay a candidate for owner for ever.
    const fresh = list.filter(s => s && Number.isFinite(s.t) && Number.isFinite(s.v) && t - s.t <= o.maxAgeMs);
    if (fresh.length) next[g] = fresh.slice(-o.window);
  }
  next[key] = pushSample(next[key], t, v, o, home);

  // Keep only the most recently heard gateways.
  const keys = Object.keys(next);
  if (keys.length > o.maxGateways) {
    const newest = (g) => next[g][next[g].length - 1].t;
    keys.sort((a, b) => newest(b) - newest(a));
    for (const g of keys.slice(o.maxGateways)) if (g !== key) delete next[g];
  }

  const levels = {};
  for (const [g, list] of Object.entries(next)) {
    const l = levelOf(list, o);
    if (l != null) levels[g] = l;
  }
  // Candidates: the beacon's own-asset gateways when it has any, else every
  // gateway. The home set is the remembered one plus whatever the live windows
  // are flagged with - the flags alone would be enough while the own tracker
  // reports, and the memory is what carries the restriction through a silence
  // longer than the window.
  //
  // An EMPTY pool is a real answer, not a miss: the asset has a gateway of its
  // own and it is not currently hearing this beacon, so there is no level. Note
  // the pool is also taken before `levels`, so an own gateway still warming up
  // (fewer than minSamples) holds ownership open rather than lending it to a far
  // gateway for a few hours and taking it back.
  const homeSet = homeSetOf(sample?.homeGateways, t, o.homeMemoryMs);
  for (const g of Object.keys(next)) if (next[g].some(s => s && s.h)) homeSet.add(g);
  const pool = homeSet.size ? Object.keys(next).filter(g => homeSet.has(g)) : Object.keys(next);
  // Strongest level wins; the id tie-break keeps two equal gateways from
  // alternating the owner on every read.
  let owner = null;
  for (const g of pool) {
    const l = levels[g];
    if (l == null) continue;
    if (owner == null || l > levels[owner] || (l === levels[owner] && g < owner)) owner = g;
  }
  const isOwner = owner != null && owner === key;
  // Ownership is decided on the LEVEL alone, deliberately unchanged by the
  // spread: the strongest link is the one that describes this stand, however
  // steady or jumpy it happens to be this hour.
  const level = isOwner ? levels[key] : null;
  const spread = (isOwner && next[key].length >= o.minSamples) ? spreadOf(next[key].map(s => s.v)) : null;
  return {
    windows: next, level, spread, floor: floorOf(level, spread, o.spreadK),
    owner, isOwner, levels, home, homeGateways: homeMem,
    // Whether this beacon has an own-asset gateway AT ALL - true even while that
    // gateway is silent and holds no window. A caller sorting readings into
    // "ours" and "overheard" needs exactly this: once an own gateway is known,
    // a read from anywhere else is overheard, whether or not ours is listening.
    hasHome: homeSet.size > 0,
  };
}

/**
 * Which devices in a history belong to the asset itself? Every non-BLE row in an
 * asset's history was written BY a device assigned to that asset, so the set of
 * their deviceIds is exactly the asset's own gateways - no extra request, and it
 * matches what the Lambda decides from the two device rows it already holds.
 *
 * A window in which the asset's own tracker sent nothing at all yields an empty
 * set, and the replay then falls back to strongest-wins for that window. That is
 * the right failure: with no own-gateway rows to compare against, the chart has
 * nothing to prefer.
 */
function ownGatewaysOf(points, explicit) {
  if (Array.isArray(explicit)) return new Set(explicit.map(String));
  const own = new Set();
  for (const p of (points || [])) {
    if (!p || p.type === 'bluetooth' || !p.deviceId) continue;
    own.add(String(p.deviceId));
  }
  return own;
}

/**
 * Browser side: stamp `_rssiAvg`, `_rssiSpread` and `_rssiFloor` on the BLE
 * points of one asset's history by replaying updateRssiLevel over them in time
 * order, per beacon.
 *
 * Also splits the RAW reading in two, because one line through every gateway's
 * reads is unreadable: `_rssiOwn` is the reading when it came from the pair the
 * level describes, `_rssiFar` when another tracker happened to overhear the tag.
 * On production data those foreign reads sit at -90..-99 dBm against a -45 dBm
 * own pair, so pooled into one series they draw a 50 dB sawtooth that looks like
 * violent signal loss and is nothing of the kind - and they stretch the axis so
 * far that the band the level actually moves in collapses to a sliver.
 *
 * `opts.homeGateways` overrides the own-gateway detection with an explicit list.
 *
 * @param {object[]} points  history points sorted ascending by `_t`
 *
 * Why recompute instead of reading the stored `rssiAvg` off the rows: the field
 * only exists on rows written after the feature shipped, so a chart that read
 * it would show a curve that starts somewhere in the middle of the history and
 * says nothing about the change the user is looking for. Replaying costs
 * nothing and makes the whole window comparable. The two agree because this is
 * the same function the Lambda folds each uplink through - the only difference
 * is the very start of the fetched window, where the replay has fewer samples
 * than the Lambda had.
 *
 * Only `type: 'bluetooth'` points take part: a tracker's own `rssi` is its
 * cellular signal, a different quantity that no rule reads.
 *
 * Mutates and returns `points` (expects them sorted ascending by `_t`).
 */
export function stampRssiLevels(points, opts = {}) {
  const own = ownGatewaysOf(points, opts.homeGateways);
  // As a list, so the replay restricts ownership over the WHOLE window, not only
  // where the own tracker happens to have reported recently - the browser knows
  // the asset's gateways from the first row, and a 40 h hole in the middle of a
  // 14 d chart must not quietly promote a neighbour's tracker there.
  const ownList = [...own];
  const byDevice = new Map();
  for (const p of (points || [])) {
    if (!p || p.type !== 'bluetooth') continue;
    const v = dbm(p.rssi);
    const t = dbm(p._t);
    if (v == null || t == null) continue;
    const dev = p.deviceId || '';
    const key = gwKey(p.gatewayDeviceId);
    const home = own.has(String(p.gatewayDeviceId ?? ''));
    const res = updateRssiLevel(byDevice.get(dev), {
      gateway: p.gatewayDeviceId, t, v, home, homeGateways: ownList,
    }, opts);
    byDevice.set(dev, res.windows);
    if (res.level != null) p._rssiAvg = res.level;
    if (res.spread != null) p._rssiSpread = res.spread;
    if (res.floor != null) p._rssiFloor = res.floor;
    // Which raw line this reading belongs on. With an owner the answer is simply
    // whether this is it. WITHOUT one - the first reads of a window, or a stretch
    // where the asset's own gateway is silent - it is foreign exactly when the
    // beacon has an own gateway to be foreign to. Asking the live windows instead
    // got this wrong in the one case that matters: after 36 h of silence the own
    // gateway's window is empty, and every overheard read was drawn on the main
    // card as if it were ours.
    const ownPair = res.owner != null ? key === res.owner : (home || !res.hasHome);
    if (ownPair) p._rssiOwn = v; else p._rssiFar = v;
  }
  return points;
}
